import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ShieldOff, Eye } from 'lucide-react';
import { AgencyAdsSidebar } from '@/components/agencies/AgencyAdsSidebar';

const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];
const TIERS = ['titanium', 'platinum', 'gold', 'silver', 'standard'];

export default function AgenciesAdPreview() {
  const { user, hasRole, loading } = useAuth();
  const allowed = hasRole('superadmin') || hasRole('ad_operations_lead') || hasRole('ad_operations_agent');

  const [city, setCity] = useState<string>('Addis Ababa');
  const [tier, setTier] = useState<string>('titanium');

  if (loading) return <Layout><div className="container py-12">Loading…</div></Layout>;

  if (!user || !allowed) {
    return (
      <Layout>
        <div className="container py-20 text-center max-w-md mx-auto">
          <ShieldOff className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-xl font-bold mb-2">Restricted</h1>
          <p className="text-sm text-muted-foreground">Ad ops & superadmins only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead title="Agencies Ad Preview" description="Simulate the Agencies page for ad targeting QA" canonical="/dev/agencies-preview" />
      <div className="container py-6 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Eye className="h-6 w-6 text-primary" /> Agencies Ad Preview
            </h1>
            <p className="text-sm text-muted-foreground">
              Simulate the Agencies page for any city + agency tier. Impressions and clicks are <strong>not</strong> logged here.
            </p>
          </div>
          <Link to="/dev/ad-events" className="text-xs text-primary hover:underline self-end">
            View raw ad events →
          </Link>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-sm">Simulation context</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs mb-1 block">Simulated city section</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Simulated agency tier</Label>
              <Select value={tier} onValueChange={setTier}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="secondary">city: {city}</Badge>
          <Badge variant="secondary" className="capitalize">tier: {tier}</Badge>
          <Badge variant="outline">page: agencies</Badge>
        </div>

        {/* Simulated layout: 3 placements side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-sm">Right Sidebar</CardTitle></CardHeader>
            <CardContent>
              <AgencyAdsSidebar
                placement="right_sidebar"
                agencyTier={tier}
                heading="Right Sidebar"
                limit={5}
                previewMode
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm">City Section ({city})</CardTitle></CardHeader>
            <CardContent>
              <AgencyAdsSidebar
                placement="agencies_city_section"
                agencyTier={tier}
                heading="City Section"
                limit={5}
                previewMode
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm">Inline Mobile</CardTitle></CardHeader>
            <CardContent>
              <AgencyAdsSidebar
                placement="agencies_inline_mobile"
                agencyTier={tier}
                heading="Inline Mobile"
                variant="horizontal"
                limit={6}
                previewMode
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
